import React, {FC} from "react";
import {useBonds} from "./useBonds";
import {DropDownCompString, getKeyVal} from "../util/DropDownComp";
import {CheckboxComponent} from "../util/CheckboxComponent";

const BondsComponent: FC = () => {
    const {
        processedBonds,
        requestSort,
        setTradedTodayOnly,
        tradedTodayOnly,
        setYtmThreshold,
        ytmThreshold,
        instrumentType,
        setInstrumentType,
        buyersAvailable,
        setBuyersAvailable,
        sellersAvailable,
        setSellersAvailable,
        refreshBonds,
        highYieldOnly,
        setHighYieldOnly,
    } = useBonds();


    function formatTimeToMaturity(expiryTimestamp: number): string {
        if (!expiryTimestamp) return "N/A";

        const expiry = new Date(expiryTimestamp);
        const now = new Date();

        if (expiry <= now) return "Expired";

        let years = expiry.getFullYear() - now.getFullYear();
        let months = expiry.getMonth() - now.getMonth();
        let days = expiry.getDate() - now.getDate();

        if (days < 0) {
            months--;
            let daysInStartMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            days += daysInStartMonth;
        }

        if (months < 0) {
            years--;
            months += 12;
        }

        const parts = [];
        if (years > 0) parts.push(`${years}Y`);
        if (months > 0) parts.push(`${months}M`);
        if (days > 0) parts.push(`${days}D`);

        return parts.length > 0 ? parts.join(',') : "Today";
    }

    function formatTimestamp(timestamp: number): string {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);
        return date.toLocaleString();
    }

    function formatLastTickTime(timestamp: number): string {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-GB');
    }

    const renderYTM = (ytm: number | null) => {
        return ytm !== null && ytm > 0 ? ytm.toFixed(2) : "N/A";
    }

    return (
        <div>
            <h2>Bonds</h2> <button onClick={refreshBonds}>Refresh</button>
            <div>
                <label>
                    <input
                        type="checkbox"
                        checked={tradedTodayOnly}
                        onChange={e => setTradedTodayOnly(e.target.checked)}
                    />
                    Traded Today
                </label>
                <label style={{marginLeft: '1rem'}}>
                    YTM {'>'}
                    <input
                        type="number"
                        value={ytmThreshold}
                        onChange={e => setYtmThreshold(parseFloat(e.target.value) || 0)}
                        style={{width: '50px', marginLeft: '5px'}}
                    />
                </label>
                <DropDownCompString text={"Type"} actual={instrumentType} set={setInstrumentType} arr={getKeyVal(["TB","GS"])} />
                <label style={{marginLeft: '1rem'}}>
                    <input
                        type="checkbox"
                        checked={buyersAvailable}
                        onChange={e => setBuyersAvailable(e.target.checked)}
                    />
                    Buyers Available
                </label>
                <label style={{marginLeft: '1rem'}}>
                    <input
                        type="checkbox"
                        checked={sellersAvailable}
                        onChange={e => setSellersAvailable(e.target.checked)}
                    />
                    Sellers Available
                </label>
                <CheckboxComponent text={"High Yield Only"} actual={highYieldOnly} val={highYieldOnly} set={setHighYieldOnly}/>
            </div>
            <table className="full">
                <thead>
                <tr>
                    <th onClick={() => requestSort('genInfo.tradingsymbol')}>Symbol</th>
                    <th onClick={() => requestSort('genInfo.coupon')}>coupon</th>
                    <th onClick={() => requestSort('genInfo.expiry')}>Expiry</th>
                    <th onClick={() => requestSort('genInfo.expiry')}>Time to Maturity</th>
                    <th onClick={() => requestSort('genInfo.exchange')}>Exchange</th>
                    <th onClick={() => requestSort('tick.last_price')}>Last Price</th>
                    <th onClick={() => requestSort('tick.ohlc.high')}>High</th>
                    <th onClick={() => requestSort('tick.ohlc.low')}>Low</th>
                    <th onClick={() => requestSort('tick.last_trade_time')}>Last Trade Time</th>
                    <th onClick={() => requestSort('tick.timestamp')}>Last Tick Time</th>
                    <th onClick={() => requestSort('tick.volume_traded')}>Volume</th>
                    <th onClick={() => requestSort('tick.average_traded_price')}>Average</th>
                    <th onClick={() => requestSort('bid')}>Bid</th>
                    <th onClick={() => requestSort('ask')}>Ask</th>
                    <th onClick={() => requestSort('ytm_bid')}>YTM (Bid)</th>
                    <th onClick={() => requestSort('ytm_ask')}>YTM (Ask)</th>
                    <th onClick={() => requestSort('ytm_last')}>YTM (Last)</th>
                    <th onClick={() => requestSort('ytm_low')}>YTM (Low)</th>
                    <th onClick={() => requestSort('ytm_high')}>YTM (High)</th>
                    <th onClick={() => requestSort('ytm_avg')}>YTM (Avg)</th>
                </tr>
                </thead>
                <tbody>
                {processedBonds.map(bond => (
                    <tr key={bond.token}>
                        <td>{bond.genInfo.tradingsymbol}</td>
                        {/*@ts-ignore*/}
                        <td>{bond.genInfo.coupon}</td>
                        {/*@ts-ignore*/}
                        <td>{formatTimestamp(bond.genInfo.expiry)}</td>
                        {/*@ts-ignore*/}
                        <td>{formatTimeToMaturity(bond.genInfo.expiry)}</td>
                        <td>{bond.genInfo.exchange}</td>
                        <td>{bond.tick.last_price}</td>
                        <td>{bond.tick.ohlc.high}</td>
                        <td>{bond.tick.ohlc.low}</td>
                        <td>{formatTimestamp(bond.tick.last_trade_time)}</td>
                        <td>{formatLastTickTime(bond.tick.timestamp)}</td>
                        <td>{bond.tick.volume_traded}</td>
                        <td>{bond.tick.average_traded_price}</td>
                        <td>{bond.tick.buy?.[0]?.price}</td>
                        <td>{bond.tick.sell?.[0]?.price}</td>
                        <td>{renderYTM(bond.ytms.bid)}</td>
                        <td>{renderYTM(bond.ytms.ask)}</td>
                        <td>{renderYTM(bond.ytms.last)}</td>
                        <td>{renderYTM(bond.ytms.low)}</td>
                        <td>{renderYTM(bond.ytms.high)}</td>
                        <td>{renderYTM(bond.ytms.avg)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default BondsComponent;
